import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, X } from "lucide-react";
import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";

import { FeedbackStatusBadge } from "@/components/feedback-status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { ImageLightbox, type LightboxSlide } from "@/components/ui/image-lightbox";
import { Spinner } from "@/components/ui/spinner";
import { Tabs } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  CATEGORY_LABELS,
  type FeedbackCategory,
  FeedbackCategory as FeedbackCategoryValues,
  type SubmitFeedbackParams,
  detectAttachmentKind,
  feedbackApi,
  formatBytes,
  validateFiles,
} from "@/lib/feedback";
import { formatDate } from "@/lib/format";
import { useObjectUrl } from "@/lib/use-object-url";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const FEEDBACK_MINE_QUERY_KEY = ["feedback", "mine"] as const;

// ---------------------------------------------------------------------------
// Main dialog trigger + container
// ---------------------------------------------------------------------------

interface FeedbackDialogProps {
  triggerClassName?: string;
}

export function FeedbackDialogTrigger({ triggerClassName }: FeedbackDialogProps): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "flex items-center text-body-3 text-muted-foreground hover:text-foreground",
          triggerClassName,
        )}
        aria-label="abrir feedback"
      >
        feedback
      </button>
      {open && <FeedbackDialog onClose={() => setOpen(false)} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Dialog body
// ---------------------------------------------------------------------------

type TabValue = "nuevo" | "mis-reportes";

function FeedbackDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const [tab, setTab] = useState<TabValue>("nuevo");
  const queryClient = useQueryClient();

  const tabs: { value: TabValue; label: string }[] = [
    { value: "nuevo", label: "nuevo feedback" },
    { value: "mis-reportes", label: "mis feedbacks" },
  ];

  return (
    <Dialog open onClose={onClose} ariaLabel="enviar feedback" className="max-w-lg">
      <header className="border-b border-border px-5 py-3">
        <h2 className="text-heading-3 font-semibold">feedback</h2>
      </header>
      <Tabs
        tabs={tabs}
        value={tab}
        onValueChange={(v) => setTab(v as TabValue)}
        className="px-5 pt-2"
      />
      <div className="overflow-y-auto" style={{ maxHeight: "calc(90vh - 160px)" }}>
        {tab === "nuevo" ? (
          <NewReportTab
            onSuccess={() => {
              void queryClient.invalidateQueries({ queryKey: FEEDBACK_MINE_QUERY_KEY });
            }}
          />
        ) : (
          <MyReportsTab />
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// "nuevo feedback" tab
// ---------------------------------------------------------------------------

/** A file the user attached, with a key that follows it however the list changes. */
interface AttachedFile {
  key: string;
  file: File;
}

// The tab owns the one lightbox of the attached images (`unify-image-lightbox`):
// the set is the image files in the order shown, videos left out. The open file
// is shown through a single object URL made here for that file alone. The
// lightbox is a second dialog on top of this one: `Dialog` makes `Escape`, the
// close control and the backdrop close only the lightbox, so the form keeps
// what the user typed and attached. It portals to `document.body`, and every
// control inside it is `type="button"`, so nothing in it can submit the form
// it is rendered in.
function NewReportTab({ onSuccess }: { onSuccess: () => void }): JSX.Element {
  const location = useLocation();
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<FeedbackCategory>(FeedbackCategoryValues.Issue);
  const [attached, setAttached] = useState<AttachedFile[]>([]);
  const nextKeyRef = useRef(0);
  // The open image's key (`null` = closed).
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const files = useMemo(() => attached.map((entry) => entry.file), [attached]);
  const imageEntries = attached.filter(
    (entry) => detectAttachmentKind(entry.file.name) === "image",
  );
  const activeFile = imageEntries.find((entry) => entry.key === activeKey)?.file ?? null;
  // One object URL, for the open file alone (the row thumbnails keep their own).
  const activeUrl = useObjectUrl(activeFile);

  const mutation = useMutation({
    mutationFn: (params: SubmitFeedbackParams) => feedbackApi.submit(params),
    onSuccess: () => {
      setSubmitted(true);
      setSubmitError(null);
      onSuccess();
    },
    onError: (error: unknown) => {
      setSubmitError(error instanceof Error ? error.message : "error inesperado al enviar");
    },
  });

  const addFiles = useCallback(
    (incoming: File[]) => {
      const merged = [...files, ...incoming];
      const errors = validateFiles(merged);
      if (errors.length > 0) {
        setFileErrors(errors.map((e) => (e.filename ? `${e.filename}: ${e.message}` : e.message)));
        return;
      }
      setFileErrors([]);
      setAttached((prev) => [
        ...prev,
        ...incoming.map((file) => {
          nextKeyRef.current += 1;
          return { key: `file-${nextKeyRef.current}`, file };
        }),
      ]);
    },
    [files],
  );

  const removeFile = (key: string): void => {
    setAttached((prev) => prev.filter((entry) => entry.key !== key));
    setFileErrors([]);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (description.trim().length === 0) return;
    const context = {
      route: location.pathname,
      user_agent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    };
    mutation.mutate({ description: description.trim(), category, context, files });
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-3 p-6 text-center">
        <p className="text-body-1 font-medium text-foreground">feedback enviado — gracias</p>
        <p className="text-body-3 text-muted-foreground">
          puedes seguir el estado en "mis feedbacks".
        </p>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setSubmitted(false);
            setDescription("");
            setCategory(FeedbackCategoryValues.Issue);
            setAttached([]);
            setFileErrors([]);
          }}
        >
          enviar otro
        </Button>
      </div>
    );
  }

  // Only the open slide has bytes; its URL is `null` for the instant between
  // choosing a file and the URL existing.
  const slides: LightboxSlide[] = imageEntries.map((entry) => ({
    key: entry.key,
    alt: entry.file.name,
    heading: entry.file.name,
    image:
      entry.file === activeFile && activeUrl !== null
        ? { status: "ready", url: activeUrl }
        : { status: "loading" },
  }));

  return (
    <form onSubmit={handleSubmit} className="p-5">
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="feedback-description" className="text-body-3 text-muted-foreground">
            descripción
          </FieldLabel>
          <Textarea
            id="feedback-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            rows={4}
            placeholder="describe el problema o propuesta…"
            className="resize-none"
          />
        </Field>

        <FieldSet>
          <FieldLegend className="text-body-3 text-muted-foreground">categoría</FieldLegend>
          <div className="flex flex-wrap gap-3">
            {Object.values(FeedbackCategoryValues).map((cat) => (
              <label key={cat} className="flex items-center gap-1.5 text-body-3 text-foreground">
                <input
                  type="radio"
                  name="feedback-category"
                  value={cat}
                  checked={category === cat}
                  onChange={() => setCategory(cat)}
                  className="size-4"
                />
                {CATEGORY_LABELS[cat]}
              </label>
            ))}
          </div>
        </FieldSet>

        <Field>
          <FieldDescription>
            adjuntos <span className="text-muted-foreground/60">(opcional, máx. 5)</span>
          </FieldDescription>
          <button
            type="button"
            onDragOver={(e: React.DragEvent<HTMLButtonElement>) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e: React.DragEvent<HTMLButtonElement>) => {
              e.preventDefault();
              setDragging(false);
              addFiles(Array.from(e.dataTransfer.files));
            }}
            className={cn(
              "flex min-h-[72px] w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-3 py-3 text-body-3 text-muted-foreground transition-colors",
              dragging
                ? "border-brand-extra-2-40 bg-muted/40"
                : "border-border hover:border-border/80",
            )}
            onClick={() => fileInputRef.current?.click()}
            aria-label="zona de arrastre para adjuntos"
          >
            <Upload size={16} aria-hidden="true" />
            <span>arrastrar o hacer clic para adjuntar</span>
            <span className="text-label-3 text-muted-foreground/60">
              imágenes (máx. 10 MiB) · videos (máx. 200 MiB)
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            aria-label="Adjuntar archivos al reporte"
            accept=".png,.jpg,.jpeg,.webp,.gif,.mp4,.mov,.webm"
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(Array.from(e.target.files));
              e.target.value = "";
            }}
          />

          {fileErrors.length > 0 && (
            <ul className="flex flex-col gap-0.5">
              {fileErrors.map((err) => (
                <li key={err} className="text-body-3 text-destructive">
                  {err}
                </li>
              ))}
            </ul>
          )}

          {attached.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {attached.map((entry) => (
                <FilePreviewRow
                  key={entry.key}
                  file={entry.file}
                  onOpen={() => setActiveKey(entry.key)}
                  onRemove={() => removeFile(entry.key)}
                />
              ))}
            </ul>
          )}
        </Field>

        {submitError && (
          <p className="text-body-3 text-destructive" role="alert">
            {submitError}
          </p>
        )}

        <div className="flex justify-end pt-1">
          <Button type="submit" disabled={mutation.isPending || description.trim().length === 0}>
            {mutation.isPending ? (
              <>
                <Spinner aria-label="enviando feedback" />
                enviando…
              </>
            ) : (
              "enviar feedback"
            )}
          </Button>
        </div>
      </FieldGroup>
      <ImageLightbox
        activeKey={activeKey}
        onActiveKeyChange={setActiveKey}
        onClose={() => setActiveKey(null)}
        slides={slides}
      />
    </form>
  );
}

interface FilePreviewRowProps {
  file: File;
  /** Opens the tab's lightbox on this file; only an image row uses it. */
  onOpen: () => void;
  onRemove: () => void;
}

function FilePreviewRow({ file, onOpen, onRemove }: FilePreviewRowProps): JSX.Element {
  const kind = detectAttachmentKind(file.name);
  const sizeLabel = formatBytes(file.size);

  const objectUrl = useMemo(
    () => (kind === "image" ? URL.createObjectURL(file) : null),
    [kind, file],
  );

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  return (
    <li className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5">
      {kind === "image" ? (
        // The thumbnail and the name are ONE control that opens the lightbox;
        // "quitar" below stays a separate sibling.
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Ver imagen: ${file.name}`}
          className="flex min-w-0 flex-1 items-center gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {objectUrl ? (
            <img
              src={objectUrl}
              alt={file.name}
              className="h-9 w-9 shrink-0 rounded object-cover"
            />
          ) : null}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-body-3 text-foreground">{file.name}</span>
            <span className="block text-label-3 text-muted-foreground">{sizeLabel}</span>
          </span>
        </button>
      ) : (
        <>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
            <span className="text-label-3">vid</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-body-3 text-foreground">{file.name}</p>
            <p className="text-label-3 text-muted-foreground">{sizeLabel}</p>
          </div>
        </>
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`quitar ${file.name}`}
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// "mis feedbacks" tab
// ---------------------------------------------------------------------------

function MyReportsTab(): JSX.Element {
  const query = useQuery({
    queryKey: FEEDBACK_MINE_QUERY_KEY,
    queryFn: () => feedbackApi.mine({ limit: 50, offset: 0 }),
  });

  if (query.isLoading) {
    return <p className="p-5 text-body-3 text-muted-foreground">cargando…</p>;
  }
  if (query.isError) {
    return (
      <p className="p-5 text-body-3 text-destructive" role="alert">
        no se pudo cargar la lista
      </p>
    );
  }

  const items = query.data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 p-6 text-center">
        <p className="text-body-2 text-muted-foreground">aún no enviaste feedback.</p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-border">
      {items.map((item) => (
        <li key={item.id} className="flex flex-col gap-1 px-5 py-3">
          <p className="text-body-2 font-medium text-foreground">{item.title}</p>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="muted">{CATEGORY_LABELS[item.category]}</Badge>
            <FeedbackStatusBadge status={item.status} />
            {item.knowledge_analysis_status && (
              <Badge variant="muted">
                análisis: {knowledgeAnalysisLabel(item.knowledge_analysis_status)}
              </Badge>
            )}
            {item.knowledge_verdict && (
              <Badge variant="muted">{knowledgeVerdictLabel(item.knowledge_verdict)}</Badge>
            )}
            <span className="text-label-3 text-muted-foreground">
              {formatRelativeDate(item.created_at)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function knowledgeAnalysisLabel(status: "pending" | "running" | "succeeded" | "failed"): string {
  const labels = {
    pending: "pendiente",
    running: "en curso",
    succeeded: "completo",
    failed: "falló",
  } as const;
  return labels[status];
}

function knowledgeVerdictLabel(verdict: "supported" | "not_supported" | "inconclusive"): string {
  const labels = {
    supported: "respaldado",
    not_supported: "no respaldado",
    inconclusive: "inconcluso",
  } as const;
  return labels[verdict];
}

function formatRelativeDate(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.floor((now - then) / 60_000);
  if (diffMin < 1) return "ahora";
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `hace ${diffHr} h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "ayer";
  if (diffDay < 7) return `hace ${diffDay} d`;
  return formatDate(iso);
}

export { FeedbackDialog };
